import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError, timer } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';

import { AuthService } from '@app/auth.service';
import { ILoginData } from '../models/login.model';
import { AppConfiguration } from './app-config.service';
import { DialogService } from './dialog.service';
import { LoggerService } from './logger.service';
import { DialogMessageComponent } from '@app/dashboard/dialog-message/dialog-message.component';

@Injectable({
  providedIn: 'root',
})
export class HttpInterceptorService implements HttpInterceptor {
  constructor(
    private dialog: MatDialog,
    private appConfig: AppConfiguration,
    private dialogService: DialogService,
    private logService: LoggerService,
    private authService: AuthService,
    private router: Router
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const userData: ILoginData | null = this.authService.getData();

    // Attach user headers if logged in
    if (userData) {
      req = req.clone({
        setHeaders: {
          'X-User-Id': this.appConfig.data.userName || this.appConfig.data.userId || '',
          'X-Court-Id': this.appConfig.data.courtId,
          Authorization: `Bearer ${this.appConfig.data.token}`,
        },
      });
    }

    return next.handle(req).pipe(
    catchError((error: HttpErrorResponse) => this.handleUnexpectedError(error))
    );
  }

  // ------------------- Central Error Handler -------------------
  protected handleUnexpectedError(error: HttpErrorResponse) {
    const status = error.status;
    const message = this.extractErrorMessage(error);

    // Log all errors internally
    this.logService.print('error', {
      status,
      message,
      url: error.url,
      backend: error.error,
    });

    switch (status) {
      // ---------- Network / timeout ----------
      case 0:
        this.openDialog('Network Error', 'Please check your internet connection and try again.');
        break;

      // ---------- Unauthorized / Forbidden ----------
      case 401:
      case 403:
        this.authService.logout();
        this.openDialog(
          'Session Ended',
          'Your session has expired or you no longer have access. Please log in again.'
        );
        // Wait 3 seconds to let the user read the message
        timer(3000).subscribe(() => this.router.navigate(['/login']));
        break;

      // ---------- Not Found ----------
      case 404:
        this.openDialog('Not Found', 'The requested resource could not be found.');
        break;

      // ---------- Internal Server Error ----------
      case 500:
        this.openDialog('Server Error', 'A server error occurred. Please try again later.');
        break;

      // ---------- Generic / Unhandled ----------
      default:
        this.openDialog(
          'Error',
          'An unexpected error occurred. Please try again later.'
        );
        break;
    }

    return throwError(() => error);
  }

  // ------------------- Extract message safely -------------------
  private extractErrorMessage(error: HttpErrorResponse): string {
    if (error.error && typeof error.error === 'string') {
      return error.error;
    }
    if (error.error && error.error.message) {
      return error.error.message;
    }
    if (error.message) {
      return error.message;
    }
    return '';
  }

  // ------------------- Unified Dialog Display -------------------
  private openDialog(title: string, message: string) {
    try {
      // Prefer your existing DialogService
      if (this.dialogService?.openDialog1) {
        this.dialogService.openDialog1({
          data: { title, message, icon: 'error' },
        });
      } else {
        // Fallback to default Material dialog
        this.dialog.open(DialogMessageComponent, {
          width: '400px',
          data: { title, message, icon: 'error' },
        });
      }
    } catch (err) {
      console.error('Failed to open dialog:', err);
    }
  }
}
